import {
    AdditionalInfoType,
    AdditionalInfoFormViewNext,
    // Narrative endpoint types
    CreateNarrative,
    CreateNarrativeResponse,
    DeleteNarrative,
    DeleteNarrativeResponse,
    ListNarratives,
    ListNarrativesResponse,
    ListNarrativesByIds,
    ListScheduledEventNarratives,
    // TODO: RND-61328 remove when rollOutCadNarrativeEndpointEnabled is enabled globally
    CreateAdditionalInfoNext,
    CreateAdditionalInfoNextResponse,
    DeleteAdditionalInfoLegacy,
    DeleteAdditionalInfoLegacyResponse,
    GetAdditionalInfosByCursor,
    GetAdditionalInfosByCursorResponse,
    GetAdditionalInfosByIds,
    GetScheduledEventsAdditionalInfos,
} from '../../types';
import { useApiClient } from '@m43/core';
import { ListDirection } from './types';
import { AxiosResponse } from 'axios-cad-nx';

interface GetInfosByCursorParams {
    agencyEventId: number;
    limit?: number;
    direction?: ListDirection;
    filter?: Array<AdditionalInfoType>;
    cursor?: number;
    includeEventNarratives?: boolean;
}

export interface AdditionalInfosResource {
    deleteInfo: (
        agencyEventId: number,
        narrativeId: number
    ) => Promise<AxiosResponse<DeleteNarrativeResponse | DeleteAdditionalInfoLegacyResponse>>;
    getInfosByCursor: (
        params: GetInfosByCursorParams
    ) => Promise<AxiosResponse<ListNarrativesResponse | GetAdditionalInfosByCursorResponse>>;
    getScheduledEventInfos: (params: {
        agencyEventId: number;
    }) => Promise<AxiosResponse<ListNarrativesResponse>>;
    createAdditionalInfo: (
        info: AdditionalInfoFormViewNext
    ) => Promise<AxiosResponse<CreateNarrativeResponse | CreateAdditionalInfoNextResponse>>;
    getInfosByIds: (
        agencyEventId: number,
        data: { ids: number[] }
    ) => Promise<AxiosResponse<ListNarrativesResponse | GetAdditionalInfosByCursorResponse>>;
}

export function useAdditionalInfosResource(
    useNarrativeEndpoints: boolean
): AdditionalInfosResource {
    const apiClient = useApiClient();

    const deleteInfo: AdditionalInfosResource['deleteInfo'] = async (
        agencyEventId,
        narrativeId
    ) => {
        if (useNarrativeEndpoints) {
            return await apiClient.request<DeleteNarrative>({
                method: 'POST',
                url: `narrative/delete`,
                data: { agencyEventId, narrativeId },
                shouldFailSilently: true,
            });
        }
        // TODO: RND-61328 remove when rollOutCadNarrativeEndpointEnabled is enabled globally
        return await apiClient.request<DeleteAdditionalInfoLegacy>({
            method: 'DELETE',
            url: `v2/events/${agencyEventId}/additional_infos/${narrativeId}/`,
            shouldFailSilently: true,
        });
    };

    const getInfosByCursor: AdditionalInfosResource['getInfosByCursor'] = async ({
        agencyEventId,
        limit: pageSize = 100,
        direction = ListDirection.HISTORIC,
        filter = [AdditionalInfoType.USER, AdditionalInfoType.SYSTEM],
        cursor: nextCursor,
        includeEventNarratives = true,
    }) => {
        if (useNarrativeEndpoints) {
            return await apiClient.request<ListNarratives>({
                method: 'POST',
                url: `narrative/list`,
                data: {
                    agencyEventId,
                    additionalInfoTypes: filter,
                    direction,
                    includeEventNarratives,
                    nextCursor,
                    pageSize,
                },
                shouldFailSilently: true,
            });
        }
        // TODO: RND-61328 remove when rollOutCadNarrativeEndpointEnabled is enabled globally
        return await apiClient.request<GetAdditionalInfosByCursor>({
            method: 'GET',
            url: `v2/next/events/${agencyEventId}/additional_infos`,
            params: { nextCursor, pageSize, direction, filter, includeEventNarratives },
            shouldFailSilently: true,
        });
    };

    const getScheduledEventInfos: AdditionalInfosResource['getScheduledEventInfos'] = async ({
        agencyEventId,
    }) => {
        if (useNarrativeEndpoints) {
            return await apiClient.request<ListScheduledEventNarratives>({
                method: 'POST',
                url: `narrative/scheduled_event/list`,
                data: { scheduledEventId: agencyEventId },
                shouldFailSilently: true,
            });
        }
        // TODO: RND-61328 remove when rollOutCadNarrativeEndpointEnabled is enabled globally
        return ((await apiClient.request<GetScheduledEventsAdditionalInfos>({
            method: 'GET',
            url: `v2/next/scheduled_events/${agencyEventId}/additional_infos`,
            shouldFailSilently: true,
        })) as unknown) as AxiosResponse<ListNarrativesResponse>;
    };

    const createAdditionalInfo: AdditionalInfosResource['createAdditionalInfo'] = async ({
        agencyEventId,
        text,
        isHighPriority,
        crossClassOptions,
        toExternalCadIntegrations,
    }) => {
        if (useNarrativeEndpoints) {
            return await apiClient.request<CreateNarrative>({
                method: 'POST',
                url: `narrative/create`,
                data: {
                    agencyEventId,
                    text,
                    priority: isHighPriority ? 'HIGH_PRIORITY_UPDATE' : 'STANDARD_UPDATE',
                    crossClassAgencyTypes: crossClassOptions,
                    toExternalCadIntegrations,
                },
                shouldFailSilently: true,
            });
        }
        // TODO: RND-61328 remove when rollOutCadNarrativeEndpointEnabled is enabled globally
        return await apiClient.request<CreateAdditionalInfoNext>({
            method: 'POST',
            url: `v2/additional_infos`,
            data: {
                agencyEventId,
                text,
                priority: isHighPriority ? 'HIGH_PRIORITY_UPDATE' : 'STANDARD_UPDATE',
                crossClassAgencyTypes: crossClassOptions,
                toExternalCadIntegrations,
            },
            shouldFailSilently: true,
        });
    };

    const getInfosByIds: AdditionalInfosResource['getInfosByIds'] = async (agencyEventId, data) => {
        if (useNarrativeEndpoints) {
            return await apiClient.request<ListNarrativesByIds>({
                method: 'POST',
                url: `narrative/list_by_ids`,
                data: { agencyEventId, narrativeIds: data.ids },
                shouldFailSilently: true,
            });
        }
        // TODO: RND-61328 remove when rollOutCadNarrativeEndpointEnabled is enabled globally
        return await apiClient.request<GetAdditionalInfosByIds>({
            method: 'POST',
            url: `v2/next/events/${agencyEventId}/additional_infos/ids`,
            data: data.ids,
            shouldFailSilently: true,
        });
    };

    return {
        createAdditionalInfo,
        deleteInfo,
        getInfosByCursor,
        getInfosByIds,
        getScheduledEventInfos,
    };
}
